import React from "react";
import { ShieldAlert, Skull, AlertTriangle, Eye, XCircle } from "lucide-react";
import { Hero, Section, Sub, Callout, NumberedItem, Bold } from "./_shared";
import { useT } from "@/i18n";

export default function SecurityPage() {
  const { lang } = useT();
  return lang === "en" ? <SecurityEN /> : <SecurityFR />;
}

// =============================================================
// FR
// =============================================================
function SecurityFR() {
  return (
    <div data-testid="view-security" className="max-w-5xl">
      <Hero
        moduleNum="03"
        eyebrowLabel="Module"
        icon={ShieldAlert}
        italic="Sécurisation"
        accent="& red flags"
        description="L'intermédiaire est, par nature, exposé à de nombreuses tentatives de fraude. Le secteur des commodités internationales est l'un des plus touchés par les escroqueries, en raison des montants importants impliqués et de la distance physique entre les parties. Ce module pourrait vous éviter des années de frustration et des pertes significatives."
      />

      <Section eyebrow="Arnaques" title="Les arnaques les plus courantes" index="3.1">
        <div className="space-y-6">
          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Arnaque N°1</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Le Faux Mandat (Fake Mandate)</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Un individu se présente comme mandataire officiel d'un grand fonds souverain, d'un gouvernement africain ou
              d'une famille royale du Golfe, avec des documents apparemment officiels à l'appui. Il vous demande de
              travailler sur un deal énorme (souvent des centaines de millions ou milliards de dollars) et vous fait
              miroiter une commission exceptionnelle. Il cherche soit à vous faire travailler des mois sans résultat
              (gaspillage de votre temps et de votre réseau), soit à vous soutirer des frais avancés.
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]"><Eye className="h-3.5 w-3.5" /> Signes distinctifs</div>
                <p className="mt-2 text-sm text-[#A1A1AA]">Documents visuellement parfaits mais avec des incohérences dans le texte (fautes d'orthographe dans les termes techniques, montants irréalistes), adresses email sur des domaines gratuits (gmail, yahoo, outlook au lieu d'un domaine d'entreprise), numéros de téléphone qui ne correspondent pas au pays indiqué, impossibilité de vérifier l'identité du mandataire via des sources tierces indépendantes.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]"><ShieldAlert className="h-3.5 w-3.5" /> Protection</div>
                <p className="mt-2 text-sm text-[#E4E4E7]">Exiger systématiquement une vidéoconférence avec le mandant direct (pas juste le mandataire). Vérifier l'entreprise dans un registre commercial officiel (Companies House UK, SEC USA, INFOGREFFE France, etc.). Rechercher le nom de l'entreprise sur Google avec les termes « scam », « fraud », « arnaque ». Demander un document officiel avec numéro de référence vérifiable.</p>
              </div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Arnaque N°2</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Le Faux Rapport SGS / Bureau Veritas</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Un rapport d'inspection SGS, Bureau Veritas ou Intertek est falsifié pour valider un produit inexistant ou
              de qualité très inférieure à ce qui est annoncé. Ces faux sont parfois extrêmement convaincants visuellement,
              avec des logos corrects et des mises en forme professionnelles.
            </p>
            <p className="mt-4 text-sm text-[#E4E4E7]">
              <Bold>Protection :</Bold> Contacter directement SGS, Bureau Veritas ou Intertek via leurs sites officiels
              (pas via un lien fourni par votre contrepartie) pour vérifier l'authenticité du rapport avec son numéro de
              référence. Ces sociétés ont des hotlines de vérification dédiées.
            </p>
            <Callout title="Règle absolue">
              <p>
                Exiger que l'inspection soit commandée et payée directement par vous ou par un tiers de confiance que vous
                choisissez, et que le rapport vous soit envoyé directement par l'inspecteur sans passer par le vendeur.
              </p>
            </Callout>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Arnaque N°3</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Les Frais Avancés (Advance Fee Fraud / Fraude 419)</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Après plusieurs semaines ou mois d'échanges sérieux qui vous ont mis en confiance, on vous demande de payer
              des frais pour « débloquer » le deal : frais douaniers, frais d'assurance-cargo, frais de notaire, caution
              bancaire, frais de conformité. Ces frais s'accumulent, à chaque fois on vous promet que c'est « le dernier
              obstacle », et le deal n'aboutit jamais.
            </p>
            <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
              Cette fraude est l'une des plus anciennes du monde des affaires internationales. Elle tire son nom du Code
              Pénal nigérian (Section 419) qui la réprime, mais elle est pratiquée dans de nombreux pays.
            </p>
            <Callout icon={AlertTriangle} title="Règle absolue et non négociable">
              <p>
                Dans un deal légitime, vous ne payez <Bold>JAMAIS</Bold> des frais avancés de votre poche personnellement.
                Les frais légitimes (inspection SGS, ouverture de LC, frais notariaux) sont toujours payés par le principal
                concerné — soit l'acheteur, soit le vendeur — et jamais par le broker.
              </p>
            </Callout>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Arnaque N°4</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Le Court-Circuit (Bypass / Circumvention)</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Après avoir mis en contact l'acheteur et le vendeur, ces derniers décident de s'entendre directement pour
              économiser votre commission. Parfois c'est évident (ils demandent vos coordonnées mutuelles), parfois c'est
              subtil (ils échangent des informations en dehors de vous, ou l'acheteur cherche votre fournisseur de manière
              indépendante).
            </p>
            <p className="mt-4 text-sm text-[#E4E4E7]">
              <Bold>Protection primaire :</Bold> ne révélez jamais les deux identités simultanément. La procédure correcte
              est : LOI signée et authentifiée → NCNDA signé par toutes les parties → IMFPA signé → introduction progressive
              et contrôlée.
            </p>
            <p className="mt-3 text-sm text-[#E4E4E7]">
              <Bold>Protection secondaire :</Bold> dans l'IMFPA, stipulez explicitement une pénalité égale à <Bold>100 % des
              commissions dues</Bold> si une transaction est réalisée directement entre les parties sans votre participation,
              sur une période de 5 ans à compter de l'introduction. Cette clause est exécutoire dans la plupart des juridictions.
            </p>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Arnaque N°5</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Le Faux LC ou Faux SBLC</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              Un acheteur présente une Lettre de Crédit ou une Stand-By Letter of Credit qui semble authentique mais est
              fictive : émise par une banque inexistante, avec de faux codes SWIFT, ou avec un format qui imite une vraie
              LC mais ne serait jamais honoré. Voir section 3.2 pour la procédure complète de vérification.
            </p>
          </div>
        </div>
      </Section>

      <Section eyebrow="Procédure" title="Comment vérifier un LC/SBLC avant d'agir" index="3.2">
        <p>Procédure de vérification en 6 étapes :</p>
        <div className="space-y-4 mt-6">
          <NumberedItem n="01" title="Identifier et vérifier la banque émettrice">
            <p>
              Rendez-vous sur <Bold>swift.com</Bold> et recherchez le code BIC/SWIFT de la banque. Si le code n'existe pas
              ou ne correspond pas à la banque indiquée sur le document, c'est immédiatement suspect. Vérifiez également
              le site web officiel de la banque et comparez les coordonnées.
            </p>
          </NumberedItem>
          <NumberedItem n="02" title="Vérifier le rating international de la banque">
            <p>
              Une LC légitime provient d'une banque classée dans le <Bold>Top 500 mondial</Bold> (classements Bloomberg,
              S&P, Moody's, Fitch). Une LC émise par une petite banque régionale inconnue, une banque offshore sans
              réputation internationale, ou une banque fondée récemment est un signal d'alarme fort.
            </p>
          </NumberedItem>
          <NumberedItem n="03" title="Demander et analyser le format SWIFT MT700">
            <p>
              Le format standard d'une Lettre de Crédit documentaire est le message <Bold>SWIFT MT700</Bold>. Demandez à
              recevoir la copie du MT700 tel qu'il a été envoyé. Vérifiez la présence et la cohérence des champs obligatoires :
              champ 20 (référence LC), champ 31D (date et lieu d'expiration), champ 32B (devise et montant), champ 41
              (disponible auprès de/par), champ 42C (tirages), champ 45A (description des marchandises), champs 46A et 47A
              (documents requis et conditions), champ 48 (délai de présentation), champ 49 (instructions de confirmation).
            </p>
          </NumberedItem>
          <NumberedItem n="04" title="Contacter directement la banque confirmante">
            <p>
              Si la LC est confirmée par une banque dans votre pays, contactez cette banque directement (via ses coordonnées
              officielles que vous trouvez vous-même, pas celles fournies par votre contrepartie) pour confirmer qu'elle a
              bien reçu et confirmé ce LC.
            </p>
          </NumberedItem>
          <NumberedItem n="05" title="Demander à votre propre banque d'authentifier">
            <p>
              Transmettez le LC à votre banque ou à la banque du vendeur et demandez-leur d'envoyer un message
              <Bold> SWIFT MT999 ou MT199</Bold> à la banque émettrice pour en demander l'authentification. C'est le moyen
              le plus sûr : si la banque émettrice répond par SWIFT et confirme le LC, c'est authentique. Si elle ne répond
              pas ou si le code SWIFT ne fonctionne pas, c'est une fraude.
            </p>
          </NumberedItem>
          <NumberedItem n="06" title="Contrôler les champs critiques">
            <p>
              Date d'expiration : doit être suffisamment longue pour permettre l'expédition et la présentation des documents.
              Port de chargement et port de destination : doivent correspondre exactement aux termes du contrat. Description
              des marchandises : doit correspondre précisément à ce qui est vendu. Conditions de paiement : doivent être
              réalisables.
            </p>
          </NumberedItem>
        </div>

        <Sub title="Signaux d'alerte sur un LC ou SBLC">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "La banque émettrice n'a pas de code SWIFT vérifiable sur swift.com",
              "L'adresse email de contact de la banque est sur un domaine gratuit (gmail, yahoo, outlook)",
              "Le montant est anormalement élevé par rapport à la taille de la transaction",
              "Les délais de validité sont très courts (moins de 30 jours pour une transaction internationale complexe)",
              "Le document vous parvient directement en PDF par email sans passer par votre propre banque",
              "On vous décourage ou vous interdit explicitement de faire vérifier le document par votre banque",
              "La banque émettrice est dans une juridiction non coopérative ou sous sanctions internationales",
            ].map((s, i) => (
              <div key={i} className="flex gap-3 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-4">
                <XCircle className="h-4 w-4 text-[#FF3B30] shrink-0 mt-0.5" />
                <span className="text-sm text-[#C4C4C7]">{s}</span>
              </div>
            ))}
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Détection" title="Reconnaître un deal bidon — la règle des 3T" index="3.3">
        <p>La règle des 3T : <Bold>Trop Beau, Trop Vite, Trop Facile.</Bold> Si un deal cumule ces trois caractéristiques, sortez.</p>

        <Sub title="Prix 20 % ou plus sous le marché">
          <p>
            Impossible sur les commodités cotées (pétrole, métaux, or). Le prix spot LME ou LBMA est public et connu de
            tous les acteurs du marché. Un vendeur ne vend pas de l'or à 20 % sous le prix LBMA par altruisme. Un prix
            anormalement bas signifie presque toujours que le produit n'existe pas ou ne correspond pas à ce qui est annoncé.
          </p>
        </Sub>
        <Sub title="Volume gigantesque pour un premier deal avec un inconnu">
          <p>
            Un vendeur sérieux ne propose pas un deal de 100 millions de dollars à quelqu'un qu'il ne connaît pas, sans
            vérification préalable, et sans avoir testé une petite transaction d'abord. Les vrais gros deals commencent
            toujours par une <Bold>transaction test</Bold>.
          </p>
        </Sub>
        <Sub title="Pression temporelle extrême">
          <p>
            « Vous avez 24 heures pour confirmer sinon on passe à un autre acheteur. » C'est une technique de manipulation
            classique visant à empêcher toute vérification sérieuse. Une vraie opportunité peut attendre 48 à 72 heures
            pour une due diligence minimale.
          </p>
        </Sub>
        <Sub title="Documents incomplets ou contradictoires">
          <p>
            Dates incohérentes entre différents documents, logos légèrement déformés, noms d'entreprises orthographiés
            différemment selon les pages, montants qui ne correspondent pas entre le contrat et la facture proforma.
          </p>
        </Sub>
        <Sub title="Refus catégorique de vidéoconférence">
          <p>
            Dans un deal légitime impliquant des millions de dollars, les parties acceptent toujours de se voir en vidéo
            pour établir la confiance. Un refus systématique de tout contact visuel est un signal d'alarme fort.
          </p>
        </Sub>
        <Sub title="Chaîne d'intermédiaires trop longue">
          <p>
            Un deal avec 5, 6, 7 intermédiaires qui se passent l'information en chaîne est soit mort (trop de monde pour
            être rentable), soit une tentative de fraude. Dans un deal sain, il ne devrait pas y avoir plus de 2 niveaux
            d'intermédiaires.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="KYC" title="Vérification des contreparties" index="3.4">
        <Sub title="Outils de vérification disponibles">
          <ul className="space-y-3">
            <li><Bold>Dun & Bradstreet (dnb.com) :</Bold> la référence mondiale pour la vérification d'entreprises. Fournit des rapports financiers détaillés, des scores de risque de crédit, la structure actionnariale, les litiges en cours. Abonnement payant mais indispensable pour les gros deals. Un rapport D&B peut être commandé à l'unité (environ 50 $ à 200 $ selon la profondeur).</li>
            <li><Bold>Companies House (gov.uk) :</Bold> gratuit et complet pour toutes les entreprises britanniques. Accès aux statuts, aux comptes déposés, à la liste des dirigeants et actionnaires, aux changements récents.</li>
            <li><Bold>INFOGREFFE (infogreffe.fr) :</Bold> pour les entreprises françaises. Extrait Kbis, statuts, comptes déposés, dirigeants. Certains documents sont payants (environ 3 à 15 €).</li>
            <li><Bold>SEC EDGAR (sec.gov) :</Bold> pour les sociétés cotées américaines. Accès à tous les dépôts réglementaires, rapports annuels (10-K), rapports trimestriels (10-Q).</li>
            <li><Bold>OpenCorporates (opencorporates.com) :</Bold> agrégateur gratuit de registres de sociétés de plus de 140 pays. Point d'entrée idéal pour une vérification rapide internationale.</li>
            <li><Bold>World-Check (Refinitiv) :</Bold> base de données payante listant les personnes et entités sous sanctions internationales, PEP (Politically Exposed Persons), personnes condamnées ou soupçonnées. Utilisée par toutes les grandes banques pour leur KYC.</li>
            <li><Bold>OFAC SDN List (ofac.treas.gov) :</Bold> la liste américaine des sanctions (Specially Designated Nationals). Gratuite et mise à jour régulièrement. Tout deal impliquant une entité sur cette liste expose à des sanctions pénales américaines graves, même si vous n'êtes pas américain.</li>
            <li><Bold>Liste des sanctions de l'Union Européenne (eur-lex.europa.eu) :</Bold> équivalent européen, obligatoire pour les entreprises opérant en Europe.</li>
          </ul>
        </Sub>

        <Sub title="Procédure KYC minimale pour tout nouveau partenaire">
          <div className="space-y-4">
            <NumberedItem n="01" title="Collecter les documents de base">
              <p>Passeport ou carte d'identité en cours de validité du ou des dirigeants, certificat d'incorporation ou
              Kbis de moins de 3 mois, preuve d'adresse de la société (facture d'utilité ou relevé bancaire officiel),
              procuration si un mandataire agit au nom des dirigeants.</p>
            </NumberedItem>
            <NumberedItem n="02" title="Vérifier l'entreprise via au minimum 2 registres officiels différents">
              <p>Pour croiser les informations.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Rechercher les dirigeants sur les listes de sanctions">
              <p>OFAC, Union Européenne, ONU. Une recherche Google avancée est également utile : « Nom de la société +
              scam », « Nom du dirigeant + fraud », « Nom de la société + review 2024 ».</p>
            </NumberedItem>
            <NumberedItem n="04" title="Demander une référence bancaire officielle">
              <p>Une lettre sur papier à en-tête de leur banque confirmant l'existence du compte et la bonne relation
              bancaire. Ce document est standard dans les milieux d'affaires sérieux.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Conserver tous les documents collectés">
              <p>Dans un dossier dédié, daté, et archivé. En cas de litige ou d'enquête, ces documents démontrent votre
              bonne foi et votre diligence.</p>
            </NumberedItem>
          </div>
        </Sub>
      </Section>
    </div>
  );
}

// =============================================================
// EN
// =============================================================
function SecurityEN() {
  return (
    <div data-testid="view-security" className="max-w-5xl">
      <Hero
        moduleNum="03"
        eyebrowLabel="Module"
        icon={ShieldAlert}
        italic="Security"
        accent="& red flags"
        description="The intermediary is, by nature, exposed to many fraud attempts. The international commodities sector is one of the most affected by scams, due to the large amounts involved and the physical distance between parties. This module could save you years of frustration and significant losses."
      />

      <Section eyebrow="Scams" title="The most common scams" index="3.1">
        <div className="space-y-6">
          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Scam #1</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">The Fake Mandate</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              An individual presents themselves as the official mandate of a major sovereign fund, an African government,
              or a Gulf royal family, with seemingly official documents to back it up. They ask you to work on a huge
              deal (often hundreds of millions or billions of dollars) and dangle an exceptional commission. They aim
              either to make you work for months without result (wasting your time and your network), or to extract
              upfront fees from you.
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]"><Eye className="h-3.5 w-3.5" /> Distinctive signs</div>
                <p className="mt-2 text-sm text-[#A1A1AA]">Visually perfect documents but with text inconsistencies (typos in technical terms, unrealistic amounts), email addresses on free domains (gmail, yahoo, outlook instead of a corporate domain), phone numbers that don't match the stated country, inability to verify the mandate's identity through independent third-party sources.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]"><ShieldAlert className="h-3.5 w-3.5" /> Protection</div>
                <p className="mt-2 text-sm text-[#E4E4E7]">Always demand a video call with the direct principal (not just the mandate). Verify the company in an official commercial registry (Companies House UK, SEC USA, INFOGREFFE France, etc.). Search the company's name on Google with the terms "scam", "fraud", "review". Demand an official document with a verifiable reference number.</p>
              </div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Scam #2</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">The Fake SGS / Bureau Veritas Report</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              An SGS, Bureau Veritas or Intertek inspection report is forged to validate a non-existent product or one of
              far lower quality than advertised. These fakes are sometimes extremely convincing visually, with correct
              logos and professional layouts.
            </p>
            <p className="mt-4 text-sm text-[#E4E4E7]">
              <Bold>Protection:</Bold> Contact SGS, Bureau Veritas or Intertek directly through their official websites
              (not via a link given by your counterparty) to verify the report's authenticity using its reference number.
              These firms have dedicated verification hotlines.
            </p>
            <Callout title="Absolute rule">
              <p>
                Demand that the inspection be ordered and paid directly by you or by a trusted third party of your
                choosing, and that the report be sent to you directly by the inspector without going through the seller.
              </p>
            </Callout>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Scam #3</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Advance Fees (Advance Fee Fraud / 419 Fraud)</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              After several weeks or months of serious exchanges that have built your trust, you're asked to pay fees to
              "unlock" the deal: customs fees, cargo insurance fees, notary fees, bank guarantee, compliance fees. These
              fees pile up, each time you're promised it's "the last hurdle", and the deal never closes.
            </p>
            <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
              This fraud is one of the oldest in international business. It takes its name from Section 419 of the
              Nigerian Criminal Code that punishes it, but it's practiced in many countries.
            </p>
            <Callout icon={AlertTriangle} title="Absolute, non-negotiable rule">
              <p>
                In a legitimate deal, you <Bold>NEVER</Bold> pay upfront fees out of your own pocket. Legitimate fees
                (SGS inspection, LC issuance, notary fees) are always paid by the relevant principal — either the buyer
                or the seller — and never by the broker.
              </p>
            </Callout>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Scam #4</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Bypass / Circumvention</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              After connecting buyer and seller, the two decide to deal directly to save your commission. Sometimes it's
              obvious (they ask for each other's contact details), sometimes it's subtle (they exchange information
              outside of you, or the buyer searches for your supplier independently).
            </p>
            <p className="mt-4 text-sm text-[#E4E4E7]">
              <Bold>Primary protection:</Bold> never reveal both identities simultaneously. The correct sequence is:
              signed and authenticated LOI → NCNDA signed by all parties → IMFPA signed → progressive, controlled
              introduction.
            </p>
            <p className="mt-3 text-sm text-[#E4E4E7]">
              <Bold>Secondary protection:</Bold> in the IMFPA, explicitly stipulate a penalty equal to <Bold>100% of the
              commissions due</Bold> if a transaction is carried out directly between the parties without your
              participation, over a 5-year period from the introduction. This clause is enforceable in most
              jurisdictions.
            </p>
          </div>

          <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
            <div className="flex items-center gap-3"><Skull className="h-5 w-5 text-[#FF3B30]" /><span className="font-mono text-[#FF3B30]">Scam #5</span></div>
            <h3 className="font-serif text-2xl mt-3 text-white">Fake LC or Fake SBLC</h3>
            <p className="mt-4 text-sm text-[#C4C4C7] leading-relaxed">
              A buyer presents a Letter of Credit or Stand-By Letter of Credit that looks authentic but is fictional:
              issued by a non-existent bank, with fake SWIFT codes, or with a format that mimics a real LC but would
              never be honoured. See section 3.2 for the full verification procedure.
            </p>
          </div>
        </div>
      </Section>

      <Section eyebrow="Procedure" title="How to verify an LC/SBLC before acting" index="3.2">
        <p>6-step verification procedure:</p>
        <div className="space-y-4 mt-6">
          <NumberedItem n="01" title="Identify and verify the issuing bank">
            <p>
              Go to <Bold>swift.com</Bold> and look up the bank's BIC/SWIFT code. If the code doesn't exist or doesn't
              match the bank named on the document, that's immediately suspicious. Also check the bank's official website
              and compare contact details.
            </p>
          </NumberedItem>
          <NumberedItem n="02" title="Check the bank's international rating">
            <p>
              A legitimate LC comes from a bank ranked in the <Bold>global Top 500</Bold> (Bloomberg, S&P, Moody's, Fitch
              rankings). An LC issued by an unknown small regional bank, an offshore bank with no international
              reputation, or a recently-founded bank is a strong red flag.
            </p>
          </NumberedItem>
          <NumberedItem n="03" title="Request and analyse the SWIFT MT700 format">
            <p>
              The standard format for a documentary Letter of Credit is the <Bold>SWIFT MT700</Bold> message. Request a
              copy of the MT700 as it was sent. Verify the presence and consistency of the mandatory fields: field 20
              (LC reference), field 31D (date and place of expiry), field 32B (currency and amount), field 41 (available
              with/by), field 42C (drafts), field 45A (description of goods), fields 46A and 47A (required documents and
              conditions), field 48 (period for presentation), field 49 (confirmation instructions).
            </p>
          </NumberedItem>
          <NumberedItem n="04" title="Contact the confirming bank directly">
            <p>
              If the LC is confirmed by a bank in your country, contact that bank directly (using official contact
              details you find yourself, not those provided by your counterparty) to confirm that it has indeed received
              and confirmed this LC.
            </p>
          </NumberedItem>
          <NumberedItem n="05" title="Ask your own bank to authenticate it">
            <p>
              Forward the LC to your bank or the seller's bank and ask them to send a <Bold>SWIFT MT999 or MT199</Bold>
              message to the issuing bank to request authentication. This is the safest method: if the issuing bank
              replies via SWIFT and confirms the LC, it's authentic. If it doesn't reply or the SWIFT code doesn't work,
              it's a fraud.
            </p>
          </NumberedItem>
          <NumberedItem n="06" title="Check the critical fields">
            <p>
              Expiry date: must be long enough to allow shipment and document presentation. Loading port and destination
              port: must match the contract terms exactly. Description of goods: must precisely match what is being sold.
              Payment conditions: must be feasible.
            </p>
          </NumberedItem>
        </div>

        <Sub title="Red flags on an LC or SBLC">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "The issuing bank has no SWIFT code verifiable on swift.com",
              "The bank's contact email is on a free domain (gmail, yahoo, outlook)",
              "The amount is abnormally high relative to the size of the transaction",
              "Validity periods are very short (less than 30 days for a complex international transaction)",
              "The document reaches you directly as a PDF by email without going through your own bank",
              "You're discouraged or explicitly forbidden from having the document verified by your bank",
              "The issuing bank is in a non-cooperative jurisdiction or under international sanctions",
            ].map((s, i) => (
              <div key={i} className="flex gap-3 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-4">
                <XCircle className="h-4 w-4 text-[#FF3B30] shrink-0 mt-0.5" />
                <span className="text-sm text-[#C4C4C7]">{s}</span>
              </div>
            ))}
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Detection" title="Spotting a phony deal — the 3T rule" index="3.3">
        <p>The 3T rule: <Bold>Too Good, Too Fast, Too Easy.</Bold> If a deal stacks all three, walk away.</p>

        <Sub title="Price 20% or more below market">
          <p>
            Impossible on quoted commodities (oil, metals, gold). The LME or LBMA spot price is public and known to
            every market actor. A seller doesn't sell gold 20% below LBMA out of altruism. An abnormally low price
            almost always means the product doesn't exist or doesn't match what is advertised.
          </p>
        </Sub>
        <Sub title="Massive volume for a first deal with a stranger">
          <p>
            A serious seller does not propose a $100M deal to someone they don't know, without prior verification, and
            without first running a small test transaction. Real big deals always start with a <Bold>test
            transaction</Bold>.
          </p>
        </Sub>
        <Sub title="Extreme time pressure">
          <p>
            "You have 24 hours to confirm or we move to another buyer." This is a classic manipulation technique aimed at
            preventing any serious verification. A real opportunity can wait 48 to 72 hours for minimal due diligence.
          </p>
        </Sub>
        <Sub title="Incomplete or contradictory documents">
          <p>
            Inconsistent dates across documents, slightly distorted logos, company names spelled differently across
            pages, amounts that don't match between the contract and the proforma invoice.
          </p>
        </Sub>
        <Sub title="Categorical refusal of video calls">
          <p>
            In a legitimate deal involving millions of dollars, parties always accept a video meeting to build trust. A
            systematic refusal of any visual contact is a strong red flag.
          </p>
        </Sub>
        <Sub title="Too long an intermediary chain">
          <p>
            A deal with 5, 6, 7 intermediaries passing information down a chain is either dead (too many people to be
            profitable) or a fraud attempt. In a healthy deal, there should be no more than 2 layers of intermediaries.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="KYC" title="Counterparty verification" index="3.4">
        <Sub title="Available verification tools">
          <ul className="space-y-3">
            <li><Bold>Dun & Bradstreet (dnb.com):</Bold> the world reference for company verification. Provides detailed financial reports, credit risk scores, shareholder structure, ongoing litigation. Paid subscription but indispensable for big deals. A D&B report can be ordered per unit (about $50 to $200 depending on depth).</li>
            <li><Bold>Companies House (gov.uk):</Bold> free and complete for all UK companies. Access to articles, filed accounts, list of directors and shareholders, recent changes.</li>
            <li><Bold>INFOGREFFE (infogreffe.fr):</Bold> for French companies. Kbis extract, articles, filed accounts, directors. Some documents are paid (about €3 to €15).</li>
            <li><Bold>SEC EDGAR (sec.gov):</Bold> for listed US companies. Access to all regulatory filings, annual reports (10-K), quarterly reports (10-Q).</li>
            <li><Bold>OpenCorporates (opencorporates.com):</Bold> free aggregator of company registries from over 140 countries. Ideal entry point for a quick international check.</li>
            <li><Bold>World-Check (Refinitiv):</Bold> paid database listing persons and entities under international sanctions, PEPs (Politically Exposed Persons), convicted or suspected persons. Used by every major bank for KYC.</li>
            <li><Bold>OFAC SDN List (ofac.treas.gov):</Bold> the US sanctions list (Specially Designated Nationals). Free and regularly updated. Any deal involving an entity on this list exposes you to serious US criminal sanctions, even if you are not American.</li>
            <li><Bold>European Union sanctions list (eur-lex.europa.eu):</Bold> European equivalent, mandatory for companies operating in Europe.</li>
          </ul>
        </Sub>

        <Sub title="Minimum KYC procedure for any new partner">
          <div className="space-y-4">
            <NumberedItem n="01" title="Collect basic documents">
              <p>Valid passport or ID for the director(s), certificate of incorporation or Kbis less than 3 months old,
              proof of company address (utility bill or official bank statement), power of attorney if a representative
              is acting on behalf of the directors.</p>
            </NumberedItem>
            <NumberedItem n="02" title="Verify the company through at least 2 different official registries">
              <p>To cross-check the information.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Check directors against sanctions lists">
              <p>OFAC, European Union, UN. An advanced Google search is also useful: "Company name + scam", "Director
              name + fraud", "Company name + review 2024".</p>
            </NumberedItem>
            <NumberedItem n="04" title="Request an official bank reference">
              <p>A letter on their bank's letterhead confirming the existence of the account and a sound banking
              relationship. This document is standard in serious business circles.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Keep all collected documents">
              <p>In a dedicated, dated and archived folder. In case of dispute or investigation, these documents
              demonstrate your good faith and diligence.</p>
            </NumberedItem>
          </div>
        </Sub>
      </Section>
    </div>
  );
}
